import { useMutation, useQuery } from "@tanstack/react-query";
import { useClient } from "../context/auth-context";
import { parseApiResponse } from "../models";
import { fetchServiceJson } from "./service-fetch";

const buildMarketplaceProductQueryParams = ({
  query,
  currency,
  limit,
  page,
  randomSeed,
  selectedFilterColors = [],
  selectedFilterSizes = [],
  selectedFilterPrice = {},
  selectedFilterCategory,
  selectedFilterCountry,
  sortOrder,
}) => {
  const queryParams = [];

  if (query) queryParams.push(`searchTerm=${query}`);
  queryParams.push("random=true");
  queryParams.push("withMedia=true");
  queryParams.push("removeOutOfStock=true");
  queryParams.push(`currency=${currency}`);
  queryParams.push(`limit=${limit}`);
  queryParams.push(`page=${page}`);
  if (page > 1 && randomSeed) queryParams.push(`randomSeed=${randomSeed}`);
  if (selectedFilterColors.length) {
    queryParams.push(`color=${selectedFilterColors.join(",")}`);
  }
  if (selectedFilterSizes.length) {
    queryParams.push(`size=${selectedFilterSizes.join(",")}`);
  }
  if (
    selectedFilterPrice?.min !== null &&
    selectedFilterPrice?.min !== undefined &&
    selectedFilterPrice?.max !== null &&
    selectedFilterPrice?.max !== undefined
  ) {
    queryParams.push(`minPrice=${selectedFilterPrice.min}`);
    queryParams.push(`maxPrice=${selectedFilterPrice.max}`);
  }
  if (selectedFilterCategory) {
    queryParams.push(`category=${selectedFilterCategory}`);
  }
  if (selectedFilterCountry) {
    queryParams.push(`country=${selectedFilterCountry}`);
  }
  if (sortOrder && (sortOrder === "asc" || sortOrder === "desc")) {
    queryParams.push("sortBy=amount");
    queryParams.push(`orderBy=${sortOrder}`);
  }

  return queryParams.length ? `?${queryParams.join("&")}` : "";
};

const buildFilterQueryParams = ({ currency, searchTerm }) => {
  const queryParams = [
    `currency=${currency}`,
    "withMedia=true",
    "removeOutOfStock=true",
  ];

  if (searchTerm) queryParams.push(`searchTerm=${searchTerm}`);
  return queryParams.length ? `?${queryParams.join("&")}` : "";
};

export async function fetchPublicProductByChecksum({
  profileId,
  checkSum,
  accessPointId,
  token,
}) {
  if (!profileId || !checkSum || !accessPointId) return null;

  const response = await fetchServiceJson(
    `product/public/organisation/${profileId}/checksum/${checkSum}/info?useFeed=true&accessPointId=${accessPointId}`,
    token,
  );

  return response ? parseApiResponse(response) : null;
}

export async function fetchPublicOrganisationProductCategories({
  profileId,
  accessPointId,
  token,
}) {
  if (!profileId || !accessPointId) return null;

  return fetchServiceJson(
    `product/public/category/organisation/${profileId}/feeds?accessPointId=${accessPointId}`,
    token,
  );
}

export function useGetProduct({ ...config }) {
  const client = useClient();
  return useMutation(
    (params) => {
      const endpoint = `product/${params.productId}/info?useFeed=true&accessPointId=${params.accessPointId}`;
      return client(endpoint, {}).then(parseApiResponse);
    },
    {
      ...config,
    },
  );
}

export function usePublicOrganisationProductFeeds({
  activeCategory,
  activeSubCategory,
  accessPointId,
  limit,
  navLocation,
  organisationId,
  page,
  query,
  randomSeed,
  token,
}) {
  const client = useClient();
  const queryKey = [
    "feeds",
    activeCategory,
    activeSubCategory,
    query,
    page,
    accessPointId,
    page > 1 ? randomSeed : null,
  ];

  return useQuery(queryKey, () => {
    const queryParams = [];
    if (activeCategory) {
      queryParams.push(`productCategoryId=${activeCategory}`);
    }
    if (activeSubCategory) {
      queryParams.push(`productSubCategoryId=${activeSubCategory}`);
    }
    if (query) {
      queryParams.push(`searchTerm=${query}`);
    }
    if (navLocation === "marketplace") {
      queryParams.push("withMedia=true");
    }
    queryParams.push(`limit=${limit}`);
    queryParams.push(`page=${page}`);
    queryParams.push("random=true");
    if (page > 1 && randomSeed) {
      queryParams.push(`randomSeed=${randomSeed}`);
    }
    queryParams.push(`accessPointId=${accessPointId}`);

    const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
    const endpoint = `product/public/organisation/${organisationId}/feeds${queryString}`;

    return client(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then((response) => parseApiResponse(response).data);
  });
}

export function usePublicOrganisationProductCategories({
  accessPointId,
  organisationId,
  token,
}) {
  const client = useClient();

  return useQuery(["_categories", accessPointId, organisationId], () => {
    return client(
      `product/public/category/organisation/${organisationId}/feeds?accessPointId=${accessPointId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    ).then((response) => parseApiResponse(response).data.productCategories);
  });
}

export function useOrganisationCategories(orgId, token, accessPointId) {
  const client = useClient();
  return useQuery(
    ["categories", orgId, accessPointId],
    async () => {
      const queryParams = new URLSearchParams();
      queryParams.append("accessPointId", accessPointId);

      const queryString = queryParams.toString();
      const url = `product/category/organisation/${orgId}/feeds?includeProducts=true${
        queryString ? `&${queryString}` : ""
      }`;

      const res = await client(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return res.data;
    },
    {
      enabled: Boolean(orgId && token && accessPointId),
      keepPreviousData: true,
    },
  );
}

export function useMarketplaceProductFeeds({
  queryKey,
  query,
  currency,
  limit,
  page,
  randomSeed,
  selectedFilterColors,
  selectedFilterSizes,
  selectedFilterPrice,
  selectedFilterCategory,
  selectedFilterCountry,
  sortOrder,
  token,
  ...config
}) {
  const client = useClient();

  return useQuery(
    queryKey,
    async () => {
      const params = buildMarketplaceProductQueryParams({
        query,
        currency,
        limit,
        page,
        randomSeed,
        selectedFilterColors,
        selectedFilterSizes,
        selectedFilterPrice,
        selectedFilterCategory,
        selectedFilterCountry,
        sortOrder,
      });
      const endpoint = `product/products${params}`;
      const response = await client(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return parseApiResponse(response).data;
    },
    {
      enabled: Boolean(token),
      keepPreviousData: true,
      ...config,
    },
  );
}

export function useHandleProducts({
  profileId,
  currency = "NGN",
  limit = 4,
  page = 1,
  token,
  enabled = true,
  ...config
}) {
  const client = useClient();

  return useQuery(
    ["handleProducts", profileId, currency, limit, page],
    async () => {
      const params = [
        `profileId=${encodeURIComponent(profileId)}`,
        "withMedia=true",
        "removeOutOfStock=true",
        `currency=${encodeURIComponent(currency)}`,
        `limit=${limit}`,
        `page=${page}`,
      ];

      return client(`product/products?${params.join("&")}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((response) => parseApiResponse(response).data);
    },
    {
      enabled: Boolean(enabled && token && profileId),
      keepPreviousData: true,
      ...config,
    },
  );
}

export function useProductPublicCategories({ token, ...config }) {
  const client = useClient();

  return useQuery(
    ["_categories", token],
    async () => {
      const response = await client(
        "configuration/public/category/product/categories",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    },
    config,
  );
}

export function useProductSearchFilters({
  queryKey,
  currency,
  searchTerm,
  token,
  ...config
}) {
  const client = useClient();

  return useQuery(
    queryKey,
    async () => {
      const params = buildFilterQueryParams({ currency, searchTerm });
      const response = await client(`product/search/filters${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    {
      enabled: Boolean(token),
      ...config,
    },
  );
}

export function useSimilarProducts({
  queryKey,
  product,
  token,
  limit,
  page,
  search,
  navLocation,
  currency,
  ...config
}) {
  const client = useClient();

  return useQuery(
    queryKey,
    async () => {
      const params = [
        `limit=${limit}`,
        `page=${page}`,
        `searchTerm=${search}`,
      ];

      if (navLocation !== "marketplace") {
        params.push(`profileId=${product.host.profileId}`);
        if (product?.accessPointId) {
          params.push(`accessPointId=${product.accessPointId}`);
        }
      }
      params.push("withMedia=true");
      params.push("removeOutOfStock=true");
      params.push(`currency=${currency}`);

      const endpoint = `product/products?${params.join("&")}`;
      const response = await client(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return parseApiResponse(response).data;
    },
    {
      enabled: Boolean(token && search),
      keepPreviousData: true,
      ...config,
    },
  );
}
